import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Chip,
  Grid,
  useMediaQuery,
  useTheme,
  LinearProgress,
  Alert,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  TrendingUp,
  BarChart,
  Download,
  Share,
  Settings,
  MoreVert,
  ExpandMore,
  ExpandLess,
  Email,
  Visibility,
  TouchApp,
  Reply,
} from '@mui/icons-material';

/**
 * Professional Email Tracking Analytics
 * Real-time tracking, engagement metrics, and detailed analytics
 */
const TrackingAnalytics = ({
  trackingData = [],
  emailStats = {},
  campaignAnalytics = {},
  onExportReport = () => {},
  onShareStats = () => {},
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [activeTab, setActiveTab] = useState(0);
  const [expandedEmailId, setExpandedEmailId] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);

  // Calculate aggregate stats
  const totalSent = trackingData.length;
  const totalOpened = trackingData.filter((t) => t.opened).length;
  const totalClicked = trackingData.filter((t) => t.clicked).length;
  const totalReplied = trackingData.filter((t) => t.replied).length;

  const openRate = totalSent > 0 ? Math.round((totalOpened / totalSent) * 100) : 0;
  const clickRate = totalSent > 0 ? Math.round((totalClicked / totalSent) * 100) : 0;
  const replyRate = totalSent > 0 ? Math.round((totalReplied / totalSent) * 100) : 0;

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 1.5 : 2 }}>
      {/* Header */}
      <Card
        variant="outlined"
        sx={{
          background: 'linear-gradient(135deg, rgba(102,126,234,0.1) 0%, rgba(118,75,162,0.1) 100%)',
          borderRadius: isMobile ? 1 : 2,
        }}
      >
        <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={1.5}
          >
            <Box>
              <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={800}>
                📊 Email Analytics
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Track opens, clicks, and engagement metrics in real-time
              </Typography>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="stretch">
              <Button
                size={isMobile ? 'small' : 'medium'}
                variant="outlined"
                startIcon={<Download />}
                onClick={onExportReport}
              >
                {isMobile ? 'Export' : 'Export Report'}
              </Button>
              <Button
                size={isMobile ? 'small' : 'medium'}
                variant="outlined"
                startIcon={<Share />}
                onClick={onShareStats}
              >
                {isMobile ? 'Share' : 'Share'}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Key Metrics */}
      <Grid container spacing={isMobile ? 1 : 1.5}>
        <Grid item xs={6} sm={3}>
          <Card variant="outlined">
            <CardContent sx={{ p: isMobile ? 1 : 1.5 }}>
              <Stack spacing={1} alignItems="center" textAlign="center">
                <Email sx={{ fontSize: 24, color: '#667eea' }} />
                <Box>
                  <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={700}>
                    {totalSent}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Sent
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={3}>
          <Card variant="outlined">
            <CardContent sx={{ p: isMobile ? 1 : 1.5 }}>
              <Stack spacing={1} alignItems="center" textAlign="center">
                <Visibility sx={{ fontSize: 24, color: '#4CAF50' }} />
                <Box>
                  <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={700}>
                    {openRate}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Open Rate
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={3}>
          <Card variant="outlined">
            <CardContent sx={{ p: isMobile ? 1 : 1.5 }}>
              <Stack spacing={1} alignItems="center" textAlign="center">
                <TouchApp sx={{ fontSize: 24, color: '#2196F3' }} />
                <Box>
                  <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={700}>
                    {clickRate}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Click Rate
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={3}>
          <Card variant="outlined">
            <CardContent sx={{ p: isMobile ? 1 : 1.5 }}>
              <Stack spacing={1} alignItems="center" textAlign="center">
                <Reply sx={{ fontSize: 24, color: '#FF9800' }} />
                <Box>
                  <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={700}>
                    {replyRate}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Reply Rate
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Engagement Overview */}
      <Card variant="outlined">
        <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
          <Typography fontWeight={700} variant="body2" mb={2}>
            📈 Engagement Overview
          </Typography>

          <Stack spacing={2}>
            {/* Open Rate Progress */}
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                <Typography variant="caption" fontWeight={600}>
                  Opens
                </Typography>
                <Typography variant="caption">
                  {totalOpened} / {totalSent}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={openRate}
                sx={{
                  borderRadius: 1,
                  height: 8,
                  backgroundColor: '#e0e0e0',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#4CAF50',
                  },
                }}
              />
            </Box>

            {/* Click Rate Progress */}
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                <Typography variant="caption" fontWeight={600}>
                  Clicks
                </Typography>
                <Typography variant="caption">
                  {totalClicked} / {totalSent}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={clickRate}
                sx={{
                  borderRadius: 1,
                  height: 8,
                  backgroundColor: '#e0e0e0',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#2196F3',
                  },
                }}
              />
            </Box>

            {/* Reply Rate Progress */}
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                <Typography variant="caption" fontWeight={600}>
                  Replies
                </Typography>
                <Typography variant="caption">
                  {totalReplied} / {totalSent}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={replyRate}
                sx={{
                  borderRadius: 1,
                  height: 8,
                  backgroundColor: '#e0e0e0',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#FF9800',
                  },
                }}
              />
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Tracking Details Tabs */}
      <Card variant="outlined">
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
            <Tab label="📧 All Emails" />
            <Tab label="👁️ Opened" />
            <Tab label="🔗 Clicked" />
            <Tab label="💬 Replied" />
          </Tabs>
        </Box>

        <CardContent sx={{ p: isMobile ? 1 : 1.5 }}>
          {trackingData.length === 0 ? (
            <Alert severity="info">No tracking data available yet</Alert>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size={isMobile ? 'small' : 'medium'}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'rgba(102,126,234,0.05)' }}>
                    <TableCell>
                      <Typography fontWeight={700} variant="caption">
                        Recipient
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography fontWeight={700} variant="caption">
                        Status
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography fontWeight={700} variant="caption">
                        Opened
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography fontWeight={700} variant="caption">
                        Clicked
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight={700} variant="caption">
                        Time
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {trackingData.map((email, idx) => (
                    <TableRow
                      key={idx}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'rgba(102,126,234,0.02)',
                        },
                      }}
                    >
                      <TableCell>
                        <Typography variant="caption" fontWeight={600}>
                          {email.recipient || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          size="small"
                          label={email.status || 'sent'}
                          variant="outlined"
                          sx={{
                            backgroundColor:
                              email.status === 'delivered' ? '#4CAF50' : '#667eea',
                            color: '#fff',
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {email.opened ? (
                          <Chip
                            size="small"
                            icon={<Visibility />}
                            label="Opened"
                            sx={{ backgroundColor: '#4CAF50', color: '#fff' }}
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {email.clicked ? (
                          <Chip
                            size="small"
                            icon={<TouchApp />}
                            label="Clicked"
                            sx={{ backgroundColor: '#2196F3', color: '#fff' }}
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="caption">{formatDate(email.sentAt)}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Campaign Performance Breakdown */}
      {Object.keys(campaignAnalytics).length > 0 && (
        <Card variant="outlined">
          <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
            <Typography fontWeight={700} variant="body2" mb={1.5}>
              🎯 Campaign Performance
            </Typography>

            <Stack spacing={1}>
              {Object.entries(campaignAnalytics).map(([campaignName, stats]) => (
                <Card key={campaignName} variant="outlined" sx={{ backgroundColor: '#fafafa' }}>
                  <CardContent sx={{ p: 1.5 }}>
                    <Typography fontWeight={600} variant="body2" mb={1}>
                      {campaignName}
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={6} sm={3}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="caption" color="text.secondary">
                            Sent
                          </Typography>
                          <Typography fontWeight={700} variant="body2">
                            {stats.sent || 0}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="caption" color="text.secondary">
                            Opens
                          </Typography>
                          <Typography fontWeight={700} variant="body2" sx={{ color: '#4CAF50' }}>
                            {stats.opens || 0}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="caption" color="text.secondary">
                            Clicks
                          </Typography>
                          <Typography fontWeight={700} variant="body2" sx={{ color: '#2196F3' }}>
                            {stats.clicks || 0}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="caption" color="text.secondary">
                            CTR
                          </Typography>
                          <Typography fontWeight={700} variant="body2" sx={{ color: '#FF9800' }}>
                            {stats.sent > 0 ? Math.round((stats.clicks / stats.sent) * 100) : 0}%
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default TrackingAnalytics;
